import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { WorkOrderMaterial } from './work-order-material.model';
import { WorkOrderMaterialService } from './work-order-material.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { ActivatedRoute } from '@angular/router';
import { getCurrentWorkOrder } from '@app/core/store/selectors/work-order.selector';
import { LoadWorkOrder } from '@dwo/actions';

interface MaterialFilters {
  articleNumber: string;
  description: string;
  status: string;
  location: string;
}

@Component({
  selector: 'app-work-order-materials',
  templateUrl: './work-order-materials.component.html',
  styleUrls: ['./work-order-materials.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WorkOrderMaterialsComponent implements OnInit, OnDestroy {
  public materials: WorkOrderMaterial[] = [];
  private destroy$ = new Subject<void>();
  groupedMaterials: any[] = [];
  filteredGroups: any[] = [];
  statusOptions: string[] = [];

  filters: MaterialFilters = {
    articleNumber: '',
    description: '',
    status: '',
    location: '',
  };

  constructor(
    private workOrderMaterialsService: WorkOrderMaterialService,
    private store: Store,
    private route: ActivatedRoute,
  ) {
    this.workOrderMaterialsService
      .getData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((materials) => {
        this.materials = materials ?? [];
        this.groupMaterials();
      });
  }

  ngOnInit() {
    this.store
      .select(getCurrentWorkOrder)
      .pipe(takeUntil(this.destroy$))
      .subscribe((workorder) => {
        if (!workorder) {
          const workOrderId = this.route.parent?.parent?.snapshot.paramMap.get('id') ?? '';
          this.store.dispatch(new LoadWorkOrder(workOrderId));
        }
      });
  }

  groupMaterials() {
    this.groupedMaterials = this.buildGroups(this.materials);
    this.statusOptions = Array.from(
      new Set((this.materials ?? []).map((m) => m.materialStatus).filter((status): status is string => !!status)),
    ).sort((a, b) => a.localeCompare(b));
    this.applyFilters();
  }

  private buildGroups(materials: WorkOrderMaterial[]): any[] {
    const map = new Map();
    for (const mat of materials ?? []) {
      if (!map.has(mat.materialNumber)) {
        map.set(mat.materialNumber, { ...mat, quantity: 0, items: [] });
      }
      const group = map.get(mat.materialNumber);
      group.quantity += mat.quantity;
      group.items.push(mat);
    }
    const groups = Array.from(map.values()).sort((a, b) => a.materialNumber.localeCompare(b.materialNumber));
    groups.forEach((group) => (group.expanded = true));
    return groups;
  }

  applyFilters(): void {
    const filtered = (this.materials ?? []).filter((material) => this.matchesFilters(material));
    this.filteredGroups = this.buildGroups(filtered);
  }

  private matchesFilters(material: WorkOrderMaterial): boolean {
    const f = this.filters;
    const articleNumber = f.articleNumber.trim().toLowerCase();
    const description = f.description.trim().toLowerCase();
    const location = f.location.trim().toLowerCase();

    const articleMatch = !articleNumber || (material.materialNumber ?? '').toLowerCase().includes(articleNumber);
    const descriptionMatch = !description || (material.description ?? '').toLowerCase().includes(description);
    const statusMatch = !f.status || material.materialStatus === f.status;
    const locationMatch =
      !location || `${material.locationNo ?? ''} ${material.locationName ?? ''}`.toLowerCase().includes(location);

    return articleMatch && descriptionMatch && statusMatch && locationMatch;
  }

  hasActiveFilters(): boolean {
    return Object.values(this.filters).some((value) => !!value);
  }

  clearFilters(): void {
    this.filters = { articleNumber: '', description: '', status: '', location: '' };
    this.applyFilters();
  }

  clearFilter(field: keyof MaterialFilters): void {
    this.filters[field] = '';
    this.applyFilters();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
